import React from 'react';

const SchemaOrg = () => {
  const baseUrl = process.env.REACT_APP_BASE_URL || 'https://houseplandirect.com';
  
  // LocalBusiness Schema
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${baseUrl}/#organization`,
    "name": "House Plan Direct",
    "url": `${baseUrl}/`,
    "logo": `${baseUrl}/logo.jpg`,
    "image": `${baseUrl}/logo.jpg`,
    "description": "House Plan Direct is a browser-based floor-plan toolkit for creating, measuring and presenting property plans — free to create and edit, £2.99 to unlock the finished project. A managed floor plan redraw service for UK property listings is also available, from £40, 24-48 hour turnaround.",
    "email": "web@houseplandirect.com",
    "priceRange": "£40-£200",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "PO Box 686",
      "addressLocality": "Exeter",
      "postalCode": "EX2 9PD",
      "addressCountry": "GB"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "50.7184",
      "longitude": "-3.5339"
    },
    "areaServed": {
      "@type": "Country",
      "name": "United Kingdom"
    },
    "sameAs": []
  };

  // WebSite Schema
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "House Plan Direct",
    "url": `${baseUrl}/`,
    "description": "Online floor plan creator and plan measurement tool. Create, measure and present a professional floor plan — free to create and edit, £2.99 to unlock the finished project. A managed floor plan redraw service is also available.",
    "publisher": {
      "@id": `${baseUrl}/#organization`
    }
  };

  // Main Service Schema - Property Marketing Floor Plans
  const propertyFloorPlanService = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${baseUrl}/#property-floor-plan-service`,
    "name": "Property Marketing Floor Plans",
    "serviceType": "Floor Plan Drawing Service",
    "description": "Professional floor plans for property listings on Rightmove, Zoopla, and property brochures. Convert your sketch into a listing-ready floor plan within 24-48 hours.",
    "url": `${baseUrl}/`,
    "provider": {
      "@id": `${baseUrl}/#organization`
    },
    "areaServed": {
      "@type": "Country",
      "name": "United Kingdom"
    },
    "audience": {
      "@type": "Audience",
      "audienceType": "Estate agents, landlords, property sellers, property photographers, EPC assessors"
    },
    "offers": {
      "@type": "Offer",
      "priceCurrency": "GBP",
      "price": "40",
      "priceSpecification": {
        "@type": "PriceSpecification",
        "price": "40",
        "priceCurrency": "GBP",
        "description": "Floor plans from £40 for up to 3 rooms"
      },
      "deliveryLeadTime": {
        "@type": "QuantitativeValue",
        "value": "24-48",
        "unitCode": "HUR"
      }
    }
  };

  // Estate Agent Floor Plans Service
  const estateAgentService = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${baseUrl}/#estate-agent-floor-plans`,
    "name": "Estate Agent Floor Plans",
    "serviceType": "Property Listing Floor Plans",
    "description": "Professional floor plans for estate agent property listings. Formatted for Rightmove, Zoopla, and property marketing. Fast 24-48 hour turnaround.",
    "url": `${baseUrl}/estate-agent-floor-plans`,
    "provider": {
      "@id": `${baseUrl}/#organization`
    },
    "areaServed": {
      "@type": "Country",
      "name": "United Kingdom"
    }
  };

  // Rightmove Floor Plan Service
  const rightmoveService = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${baseUrl}/#rightmove-floor-plans`,
    "name": "Rightmove Floor Plan Service",
    "serviceType": "Property Portal Floor Plans",
    "description": "Floor plans formatted for Rightmove property listings. Increase buyer engagement with professional floor plans. 24-48 hour delivery.",
    "url": `${baseUrl}/rightmove-floor-plan-service`,
    "provider": {
      "@id": `${baseUrl}/#organization`
    },
    "areaServed": {
      "@type": "Country",
      "name": "United Kingdom"
    }
  };

  // Product Schema for Pricing
  // priceValidUntil must always be a future date or Google's Rich Results
  // validator flags the offer as expired — compute it relative to "now"
  // (1 year out) instead of a hard-coded date that goes stale.
  const priceValidUntil = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const floorPlanProduct = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Property Floor Plan",
    "image": `${baseUrl}/logo.jpg`,
    "description": "Professional floor plan for property marketing. Delivered in JPEG, PDF, and DWG formats within 24-48 hours.",
    "brand": {
      "@type": "Brand",
      "name": "House Plan Direct"
    },
    "offers": {
      "@type": "Offer",
      "priceCurrency": "GBP",
      "price": "40",
      "priceValidUntil": priceValidUntil,
      "availability": "https://schema.org/InStock",
      "url": `${baseUrl}/pricing`
    }
  };

  // HowTo Schema
  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Get a Floor Plan for Your Property Listing",
    "description": "Simple 3-step process to convert your sketch into a professional floor plan for Rightmove, Zoopla, and property brochures",
    "totalTime": "PT48H",
    "estimatedCost": {
      "@type": "MonetaryAmount",
      "currency": "GBP",
      "value": "40"
    },
    "step": [
      {
        "@type": "HowToStep",
        "position": 1,
        "name": "Upload your sketch",
        "text": "Upload your hand-drawn sketch, photo, or old floor plan through our website"
      },
      {
        "@type": "HowToStep",
        "position": 2,
        "name": "We redraw your plan",
        "text": "Our team converts your sketch into a professional, listing-ready floor plan"
      },
      {
        "@type": "HowToStep",
        "position": 3,
        "name": "Receive listing-ready floor plan",
        "text": "Get your professional floor plan in JPEG, PDF, and DWG formats within 24-48 hours"
      }
    ]
  };

  // FAQ Schema for Homepage
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Is it free to create my own floor plan?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. The Floor Plan Creator and Plan Measure are both free to use for creating, measuring and editing. A one-off £2.99 unlocks the finished, watermark-free project — no subscription."
        }
      },
      {
        "@type": "Question",
        "name": "Can I measure an existing floor plan online?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Upload a PDF or image, calibrate it against one known dimension, then measure lengths, areas and rooms for free directly in your browser with Plan Measure."
        }
      },
      {
        "@type": "Question",
        "name": "What is a property marketing floor plan?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "A property marketing floor plan is a professional 2D drawing showing the layout of a property. It's used by estate agents on Rightmove, Zoopla, and in property brochures to help buyers visualise the space and room layout."
        }
      },
      {
        "@type": "Question",
        "name": "Do estate agents need floor plans for listings?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, floor plans significantly increase buyer engagement. Properties with floor plans receive more enquiries and viewings. Rightmove and Zoopla both support and recommend floor plans for property listings."
        }
      },
      {
        "@type": "Question",
        "name": "How much does a property floor plan cost in the UK?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "At House Plan Direct, floor plans start from just £40 for up to 3 rooms. Additional rooms cost £5 each. This includes delivery in JPEG, PDF, and DWG formats within 24-48 hours."
        }
      },
      {
        "@type": "Question",
        "name": "Can a sketch be converted into a professional floor plan?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! Simply upload your hand-drawn sketch, a photo of your floor plan, or an old plan. We'll convert it into a professional, listing-ready floor plan suitable for Rightmove, Zoopla, and property brochures."
        }
      },
      {
        "@type": "Question",
        "name": "How long does it take to create a floor plan?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Standard turnaround is 24-48 hours. We offer fast delivery for urgent property listings and quick house sales."
        }
      },
      {
        "@type": "Question",
        "name": "Where can I get a floor plan for selling a house in the UK?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "House Plan Direct provides professional floor plans for house sales across the UK. Simply upload your sketch and receive a listing-ready floor plan within 24-48 hours. Prices start from £40."
        }
      }
    ]
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(propertyFloorPlanService)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(estateAgentService)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(rightmoveService)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(floorPlanProduct)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(howToSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
    </>
  );
};

export default SchemaOrg;
